import { type CSSProperties, type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { HotTable as HotTable, type HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import type Handsontable from 'handsontable';
import type { CellChange, CellValue, ChangeSource, ColumnSettings, GridSettings, RangeType } from '@/support/hot.types';
import { registerHOTModules, rowHeaderWithControlsRenderer } from '@/support/hot';
import { validators } from '@/support/hot.validators';
import {
  isSampleData,
  type SampleDefinitionRowData,
  type SamplePlanState,
  useAddSamplePlanContext,
  usePlannedSample,
} from '@/components/Samples/AddSamplePlanProvider';
import { generateName } from '@/components/Samples/SampleDefinition.utils';
import { _isEmpty, _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import { type TeamGlossaryApiId } from '@/model/Glosary.model';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { SampleTypeSelector } from '@/components/Samples/SampleTypeSelector';
import { type MetadataGlossaryV1 } from '@/model/Metadata.model';
import { fromInVivoValidator, type InVivoValidator, readInVivoValidatorResult } from '@/support/hotValidator.util';
import { multiSelectAfterPasteHandler } from '@/components/UI/SpreadSheet/multiSelectCellTransport';
import { timepointAfterPasteHandler, TimepointEditor } from '@/components/UI/SpreadSheet/editors/TimepointEditor';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';
import { createExcludeSpareRowsFromCopyHook } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { generateTempApiId } from '@/utils/ApiId.utils';
import { byMetadataPosition } from '@/utils/MetadataOrder.utils';
import {
  type SampleDefinitionApiId,
  type SampleDetailType,
  type TempSampleDefinitionApiId,
  type TempSampleTypeId,
} from '@/model/SamplePlan.model';
import { renderToString } from 'react-dom/server';
import styles from './AddSamplePlanGroup.module.scss';
import useElementBinding from '@/support/Hooks/utils/useElementBinding';

registerHOTModules();
TimepointEditor.register();

type ValidatorFn = (this: unknown, value: unknown, callback: (valid: boolean) => void) => void;

const valueToArray = (value: unknown): Array<string> => {
  if (Array.isArray(value)) {
    return value.reduce<Array<string>>((acc, v) => {
      if (typeof v === 'string') {
        const trimmed = v.trim();
        if (_isNotEmpty(trimmed)) {
          acc.push(trimmed);
        }
      } else if (typeof v === 'number') {
        acc.push(`${v}`);
      }
      return acc;
    }, []);
  }
  if (typeof value === 'string') {
    return value.split(',').reduce<Array<string>>((acc, v) => {
      const trimmed = v.trim();
      if (_isNotEmpty(trimmed)) {
        acc.push(trimmed);
      }
      return acc;
    }, []);
  }
  return [];
};

const nameInVivoValidatorGenerator =
  (samplePlanState: SamplePlanState): InVivoValidator =>
  async (value) => {
    if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (trimmedValue.length === 0) {
        return { valid: false, message: 'Name is required' };
      }

      if (trimmedValue.length > 255) {
        return { valid: false, message: 'Must be 255 characters or less' };
      }

      const isUnique = samplePlanState.isUniqueDefinitionName(trimmedValue);
      if (!isUnique) {
        return { valid: false, message: 'Must be unique' };
      }

      return { valid: true };
    } else {
      return { valid: false, message: 'Must be a text value' };
    }
  };

const detailInVivoValidator: InVivoValidator = async (value, columnSettings) => {
  const required = columnSettings.required === true;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return { valid: false, message: 'Must be a valid number' };
    }
    if (value <= 0) {
      return { valid: false, message: 'Must be a positive number' };
    }
    return { valid: true };
  }

  const stringValue = `${value ?? ''}`;
  const hasValue = _isNotBlank(stringValue);

  if (required && !hasValue) {
    return { valid: false, message: 'This field is required' };
  }

  if (hasValue) {
    const numValue = Number(stringValue);
    if (!Number.isFinite(numValue)) {
      return { valid: false, message: 'Must be a valid number' };
    }
    if (numValue <= 0) {
      return { valid: false, message: 'Must be a positive number' };
    }
  }
  return { valid: true };
};

const calculateCollectionTimeInVivoValidator: InVivoValidator = async (value, columnSettings) => {
  if (_isNil(value)) {
    return { valid: true };
  }
  if (typeof value === 'boolean') {
    if (value) {
      const timepointsColumn = columnSettings.instance.propToCol('timepoints');
      const timepoints = columnSettings.instance.getDataAtCell(columnSettings.row, timepointsColumn);

      if (Array.isArray(timepoints) && timepoints.length > 0) {
        return { valid: true };
      }
      return { valid: false, message: 'Can only calculate collection time when 1 or more timepoints are configured.' };
    }
    return { valid: true };
  }
  return { valid: false, message: 'Must be a boolean value' };
};

const calculateCollectionTimeValidator = fromInVivoValidator(calculateCollectionTimeInVivoValidator) as ValidatorFn;

const checkboxAfterPasteHandler = function (
  this: Handsontable.Core,
  data: Array<Array<CellValue>>,
  coords: Array<RangeType>
): void {
  if (_isEmpty(coords) || _isEmpty(data)) {
    return;
  }

  try {
    const cellUpdates: Array<[number, number, boolean]> = [];

    for (const { startRow, startCol, endRow, endCol } of coords) {
      // HoT reports endRow === startRow when multiple rows are pasted into a single selected
      // cell; extend to cover every pasted data row (parity with the pre-v16 handler).
      const actualEndRow = Math.max(endRow, startRow + data.length - 1);
      for (let row = startRow; row <= actualEndRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          const cellMeta = this.getCellMeta(row, col);

          if (cellMeta.type === 'checkbox') {
            const dataRowIndex = (row - startRow) % data.length;
            const dataColIndex = (col - startCol) % (data[0]?.length ?? 1);
            const pastedValue = data[dataRowIndex]?.[dataColIndex];

            if (_isNil(pastedValue)) {
              continue;
            }

            const boolValue =
              typeof pastedValue === 'boolean' ? pastedValue : String(pastedValue).trim().toLowerCase() === 'true';
            cellUpdates.push([row, col, boolValue]);
          }
        }
      }
    }

    if (_isNotEmpty(cellUpdates)) {
      this.setDataAtCell(cellUpdates, 'edit');
      setTimeout(() => this.validateCells(), 100);
    }
  } catch (error) {
    ExceptionHandler.captureException(
      new InVivoError('Error in checkbox paste handler', {
        slug: 'checkbox-paste-handler-error',
        cause: error,
      })
    );
  }
};

const validationError = (prop: string | number): string => {
  if (typeof prop === 'string') {
    switch (prop) {
      case 'name':
        return 'must be unique and between 1 and 255 characters long.';
      case 'weight':
      case 'volume':
      case 'width':
      case 'length':
      case 'depth':
        return 'Must be a positive number.';
    }
  }
  return 'Invalid value.';
};

const ERROR_TOOLTIP_ID = 'global-tooltip-id';
const INVALID_CLASS = 'htInvalid';

const cellKey = (row: number, column: number): string => `${row},${column}`;

const clearCellError = (cell: HTMLTableCellElement): void => {
  cell.removeAttribute('data-tooltip-id');
  cell.removeAttribute('data-type');
  cell.removeAttribute('data-tooltip-content');
  cell.classList.remove(INVALID_CLASS);
};

const applyCellError = (cell: HTMLTableCellElement, message: string): void => {
  cell.setAttribute('data-tooltip-id', ERROR_TOOLTIP_ID);
  cell.setAttribute('data-type', 'error');
  cell.setAttribute('data-tooltip-content', message);
  cell.classList.add(INVALID_CLASS);
};

const hasSignificantData = (definition: SampleDefinitionRowData): boolean => {
  return Object.entries(definition).some(([key, value]) => {
    if (key === 'api_id' || key === 'name' || key === 'calculate_collection_time') {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'boolean') {
      return value === true;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) && value > 0;
    }
    if (typeof value === 'string') {
      return _isNotBlank(value);
    }

    return _notNil(value);
  });
};

const minSpareRows = 1;

export const defaultTableSettings: GridSettings = {
  manualColumnResize: true,
  manualRowResize: true,
  height: 'auto',
  allowInsertRow: true,
  minSpareRows,
  hiddenColumns: { copyPasteEnabled: false, columns: [0] },
  modifyCopyableRange: createExcludeSpareRowsFromCopyHook(minSpareRows),
  rowHeaders: true,
  autoColumnSize: { useHeaders: true },
  columnHeaderHeight: 50,
};

const MetadataTitle: FC<{ metadataGlossary: MetadataGlossaryV1 }> = ({ metadataGlossary }) => (
  <span>
    {metadataGlossary.title}
    {metadataGlossary.required ? <span className="required ml1">(req.)</span> : null}
  </span>
);

const metadataTitle = (metadataGlossary: MetadataGlossaryV1) =>
  renderToString(<MetadataTitle metadataGlossary={metadataGlossary} />);

const metadataGlossaryComparator = byMetadataPosition<MetadataGlossaryV1>(
  (glossary) => glossary.display_order,
  (glossary) => glossary.title
);

const metadataGlossaryToColumn = (metadataGlossary: MetadataGlossaryV1): ColumnSettings => {
  switch (metadataGlossary.field_type) {
    case 'numeric': {
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: 'numeric',
        required: metadataGlossary.required,
        validator: validators.metadata.number,
      };
    }
    case 'date': {
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: 'date',
        renderer: 'in-vivo-date',
        dateFormat: 'YYYY-MM-DD',
        required: metadataGlossary.required,
        validator: validators.metadata.date,
      };
    }
    case 'select': {
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: MultiSelectEditor.type,
        width: 200,
        isMultiSelect: false,
        options: metadataGlossary.options ?? [],
        required: metadataGlossary.required,
      };
    }
    case 'multi_select': {
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: MultiSelectEditor.type,
        width: 200,
        isMultiSelect: true,
        jsonEncoded: true,
        options: metadataGlossary.options ?? [],
        required: metadataGlossary.required,
      };
    }
    default:
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: 'text',
        validator: validators.metadata.text,
        required: metadataGlossary.required,
      };
  }
};

export const AddSamplePlanGroup: FC<{ type: TeamGlossaryApiId | TempSampleTypeId }> = ({ type }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hotRef = useRef<HotTableRef | null>(null);

  useElementBinding({
    field: 'hot',
    element: containerRef,
    accessor: useCallback(() => hotRef.current?.hotInstance, [hotRef]),
  });

  const { samplePlanState, sampleGlossary, metadataGlossary, enable_collection_time_calculation } =
    useAddSamplePlanContext();

  const plannedSampleData = usePlannedSample(type);

  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (_notNil(hot)) {
      hot.addHook('afterPaste', timepointAfterPasteHandler);
      hot.addHook('afterPaste', multiSelectAfterPasteHandler);
      hot.addHook('afterPaste', checkboxAfterPasteHandler);

      return () => {
        try {
          if (!hot.isDestroyed) {
            hot.removeHook('afterPaste', timepointAfterPasteHandler);
            hot.removeHook('afterPaste', multiSelectAfterPasteHandler);
            hot.removeHook('afterPaste', checkboxAfterPasteHandler);
          }
        } catch (error) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to remove Handsontable paste hooks during cleanup', {
              cause: error,
              slug: 'hot-cleanup-error',
            })
          );
        }
      };
    }
  }, [hotRef]);

  const { plannedSampleCompositeId } = useMemo(() => {
    if (isSampleData(plannedSampleData)) {
      return {
        plannedSampleCompositeId: (
          (sampleGlossary.get(plannedSampleData.type)?.options?.details ?? []) as Array<SampleDetailType>
        )
          .map((detail) => `${detail}_${plannedSampleData[detail] ?? '<missing>'}`)
          .join('__'),
      };
    }
    return { plannedSampleCompositeId: null };
  }, [plannedSampleData, sampleGlossary]);

  const tableSettings = useMemo<GridSettings | null>(() => {
    if (_notNil(plannedSampleData) && _notNil(plannedSampleCompositeId) && isSampleData(plannedSampleData)) {
      const detailColumns = (
        (sampleGlossary.get(plannedSampleData.type)?.options?.details ?? []) as Array<SampleDetailType>
      ).reduce<Array<ColumnSettings>>((acc, detail) => {
        if (detail in plannedSampleData) {
          acc.push({
            title: `${detail.charAt(0).toUpperCase()}${detail.slice(1)} (${plannedSampleData[detail]})`,
            data: detail,
            readOnly: false,
            type: 'numeric',
            validator: fromInVivoValidator(detailInVivoValidator) as ValidatorFn,
          });
        }
        return acc;
      }, []);
      const metadataColumns = Array.from(metadataGlossary.values())
        .sort(metadataGlossaryComparator)
        .reduce<Array<ColumnSettings>>((acc, glossary) => {
          return [...acc, metadataGlossaryToColumn(glossary)];
        }, []);
      const columns: Array<ColumnSettings> = [
        { data: 'api_id' },
        {
          title: 'Name',
          width: 200,
          data: 'name',
          type: 'text',
          validator: fromInVivoValidator(nameInVivoValidatorGenerator(samplePlanState)) as ValidatorFn,
        },
        {
          title: 'Timepoints',
          data: 'timepoints',
          type: TimepointEditor.type,
          allow_negative: true,
          allow_zero: true,
        },
        ...(enable_collection_time_calculation
          ? [
              {
                title: 'Calculate collection time',
                data: 'calculate_collection_time',
                type: 'checkbox',
                validator: calculateCollectionTimeValidator,
              },
            ]
          : []),
        ...detailColumns,
        ...metadataColumns,
      ];
      return {
        ...defaultTableSettings,
        columns,
      };
    }
    return null;
  }, [
    metadataGlossary,
    plannedSampleCompositeId,
    plannedSampleData,
    sampleGlossary,
    samplePlanState,
    enable_collection_time_calculation,
  ]);

  const afterChangeHook = useCallback(
    function (this: Handsontable.Core, changes: Array<CellChange> | null, source: ChangeSource) {
      if (source === 'loadData') {
        setTimeout(() => this.validateCells(), 200);
        return;
      }
      const currentSampleData = samplePlanState.getPlannedSample(type);
      if (isSampleData(currentSampleData)) {
        const sourceData = (this.getSourceData() as Array<SampleDefinitionRowData>).slice(0, -1);
        const definitions: Array<SampleDefinitionRowData> = [];
        const apiIdUpdates: Array<[number, number, SampleDefinitionRowData['api_id']]> = [];
        const nameUpdates: Array<[number, number, string]> = [];
        const existingApiIds = new Set<SampleDefinitionApiId | TempSampleDefinitionApiId>();

        sourceData.forEach((definition, index) => {
          const apiIdSet = _isNotBlank(definition.api_id);
          const isDuplicate = apiIdSet && existingApiIds.has(definition.api_id);
          const api_id = apiIdSet && !isDuplicate ? definition.api_id : generateTempApiId('sdf');
          if (!apiIdSet || isDuplicate) {
            apiIdUpdates.push([index, 0, api_id]);
          }
          existingApiIds.add(api_id);

          if (!_isNotBlank(definition.name) && source === 'edit') {
            if (hasSignificantData(definition)) {
              const sampleTypeName = sampleGlossary.get(currentSampleData.type)?.title ?? '';
              const nameCol = this.propToCol('name');
              const autoName = generateName(sampleTypeName, definitions);
              nameUpdates.push([index, nameCol as number, autoName]);
              definition.name = autoName;
            }
          }

          definitions.push({
            ...definition,
            api_id,
            timepoints: valueToArray(definition.timepoints),
            calculate_collection_time:
              typeof definition.calculate_collection_time === 'boolean' ? definition.calculate_collection_time : false,
          });
        });
        samplePlanState.setSampleData(
          {
            ...currentSampleData,
            definitions,
          },
          undefined,
          'hot'
        );
        if (apiIdUpdates.length > 0 || nameUpdates.length > 0) {
          const incomingUpdates: Array<[number, number, CellValue]> =
            changes?.map((change) => [change[0], this.propToCol(change[1] as string | number) as number, change[3]]) ??
            [];
          window.requestAnimationFrame(() => {
            this.setDataAtCell([...apiIdUpdates, ...nameUpdates, ...incomingUpdates], source);
          });
        }

        if (enable_collection_time_calculation) {
          const timepointChanges = changes?.filter((change) => change[1] === 'timepoints') ?? [];
          if (_isNotEmpty(timepointChanges)) {
            const calcTimeCol = this.propToCol('calculate_collection_time');
            window.requestAnimationFrame(() => {
              timepointChanges.forEach((change) => {
                const row = change[0];
                this.validateCell(
                  this.getDataAtCell(row, calcTimeCol as number),
                  this.getCellMeta(row, calcTimeCol as number),
                  () => {},
                  'timepoint-change'
                );
              });
            });
          }
        }
      }
    },
    [samplePlanState, type, sampleGlossary, enable_collection_time_calculation]
  );

  const invalidCellsRef = useRef<Map<string, string>>(new Map());

  const afterValidateHook = useCallback(
    function (this: Handsontable.Core, isValid: boolean, value: CellValue, row: number, prop: string | number) {
      const column = this.propToCol(prop);
      const hasColumn = typeof column === 'number' && column >= 0;
      const key = hasColumn ? cellKey(row, column) : null;
      const cell = hasColumn ? this.getCell(row, column) : null;

      // A row without an api_id is the trailing spare row Handsontable keeps for the next entry,
      // which must never be reported as invalid.
      const valid = isValid || _isNil(this.getDataAtCell(row, 0));

      if (valid) {
        if (_notNil(key)) {
          invalidCellsRef.current.delete(key);
        }
        if (_notNil(cell)) {
          clearCellError(cell);
        }
      } else {
        // The validator recorded why it failed on the cell's meta; reading it back avoids re-running
        // the validator, whose late result could otherwise be written to a recycled cell element.
        const recorded = hasColumn ? readInVivoValidatorResult(this.getCellMeta(row, column), value) : undefined;
        const message = _notNil(recorded) && !recorded.valid ? recorded.message : validationError(prop);

        if (_notNil(key)) {
          invalidCellsRef.current.set(key, message);
        }
        if (_notNil(cell)) {
          applyCellError(cell, message);
        }
      }

      samplePlanState.setValidation(type as TeamGlossaryApiId, `${row}.${prop}`, valid);
      return valid;
    },
    [samplePlanState, type]
  );

  /**
   * Restores the error styling onto a cell Handsontable has just repainted. Every render pass blanks
   * `TD.className`, and the custom renderers this grid relies on (timepoints, multi-select) do not
   * run the base renderer that would otherwise reinstate `htInvalid` from the cell's meta. Without
   * this, validating any one column would strip the styling from every invalid cell on screen.
   */
  const afterRendererHook = useCallback((cell: HTMLTableCellElement, row: number, column: number): void => {
    const message = invalidCellsRef.current.get(cellKey(row, column));
    if (_isNil(message)) {
      clearCellError(cell);
      return;
    }
    applyCellError(cell, message);
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    samplePlanState.addEventListener(
      'duplicateSampleDefinitionNamesDetected',
      () => {
        const hot = hotRef.current?.hotInstance;
        if (_notNil(hot)) {
          hot.validateColumns([hot.propToCol('name') as number]);
        }
      },
      {
        signal: abortController.signal,
      }
    );
    return () => abortController.abort();
  }, [hotRef, samplePlanState]);

  const afterCreateRowHook = useCallback(
    function (this: Handsontable.Core, index: number, amount: number, source?: ChangeSource) {
      const currentSampleData = samplePlanState.getPlannedSample(type);

      if (_notNil(currentSampleData) && isSampleData(currentSampleData)) {
        const sampleTypeName = sampleGlossary.get(currentSampleData.type)?.title ?? '';

        if ((source as string) === 'add-row.click' && index > 0) {
          const newApiId = generateTempApiId('sdf');
          const newName = generateName(sampleTypeName, currentSampleData.definitions);

          const colCount = this.countCols();
          const updates: Array<[number, number, CellValue]> = [];

          for (let col = 0; col < colCount; col++) {
            const prop = this.colToProp(col);
            if (prop === 'api_id') {
              updates.push([index, col, newApiId]);
            } else if (prop === 'name') {
              updates.push([index, col, newName]);
            } else {
              const value = this.getDataAtCell(index - 1, col);
              if (_notNil(value)) {
                updates.push([index, col, value]);
              }
            }
          }

          this.setDataAtCell(updates, source);
        }
      }

      if ((source as string) === 'add-row.click' || source === 'auto') {
        // Inserting above an existing row shifts every row below it, so errors recorded against the
        // old layout now describe the wrong cells. Re-derive them from the grid instead.
        if ((source as string) === 'add-row.click') {
          invalidCellsRef.current.clear();
        }
        setTimeout(() => {
          this.validateCells();
        }, 100);
      }
    },
    [samplePlanState, type, sampleGlossary]
  );

  const afterRemoveRowHook = useCallback(
    function (
      this: Handsontable.Core,
      index: number,
      amount: number,
      physicalRows: Array<number>,
      source?: ChangeSource
    ) {
      if ((source as string) === 'remove-row.click') {
        const currentSampleData = samplePlanState.getPlannedSample(type);
        if (isSampleData(currentSampleData)) {
          const data = {
            ...currentSampleData,
            definitions: this.getSourceData().slice(0, -1) as Array<SampleDefinitionRowData>,
          };
          samplePlanState.setSampleData(data, undefined, 'hot');
        }
        // Row indices shift when a row is removed, so errors recorded against the old layout now
        // describe the wrong cells. Re-derive them from the grid instead.
        invalidCellsRef.current.clear();
        setTimeout(() => {
          const rootElement = this.rootElement;
          if (_notNil(rootElement)) {
            rootElement.querySelectorAll(`.${INVALID_CLASS}`).forEach((el) => {
              el.classList.remove(INVALID_CLASS);
              el.removeAttribute('data-tooltip-id');
              el.removeAttribute('data-type');
              el.removeAttribute('data-tooltip-content');
            });
          }
          this.validateCells();
        }, 100);
      }
    },
    [samplePlanState, type]
  );

  const MemoizedHotTable = useMemo(() => {
    const sampleData = samplePlanState.getPlannedSample(type);
    return (
      <HotTable
        ref={hotRef}
        data={isSampleData(sampleData) ? [...(sampleData.definitions ?? []), {}] : [{}]}
        licenseKey={AppConfig.handsOnTableLicence}
        afterChange={afterChangeHook}
        afterValidate={afterValidateHook}
        afterCreateRow={afterCreateRowHook}
        afterRemoveRow={afterRemoveRowHook}
        afterRenderer={afterRendererHook}
        afterGetRowHeader={rowHeaderWithControlsRenderer}
        {...(tableSettings ?? {})}
      />
    );
  }, [
    samplePlanState,
    type,
    tableSettings,
    afterChangeHook,
    afterValidateHook,
    afterCreateRowHook,
    afterRemoveRowHook,
    afterRendererHook,
  ]);

  return (
    <div
      data-test-component="AddSamplePlanGroup"
      data-test-element="container"
      className="bg-near-white pa3 mv2"
      data-test-key={type}
      ref={containerRef}
    >
      <SampleTypeSelector type={type} />
      {_notNil(tableSettings) ? (
        <div>
          <div
            className={`ow-spreadsheet-styles mt4 ${styles.hotContainer}`}
            style={{ '--hot-column-height': `${tableSettings?.columnHeaderHeight ?? 50}px` } as CSSProperties}
            data-test-element="hot-container"
          >
            {MemoizedHotTable}
          </div>
        </div>
      ) : null}
    </div>
  );
};
